/**
 * useIsPro — small hook that returns the caller's Pro status.
 *
 * Caches the /api/billing/me response in the component lifecycle. Returns
 * `null` while loading, `true` for pro/legacy_pro, `false` for free.
 *
 * Also exposes `requirePro(featureUrl, action)`: a guard that fires the
 * `markovlab:upgrade-required` event for free users and runs `action()` for
 * Pro users. Used to gate CTAs *before* the API call instead of waiting
 * for a 402 round-trip.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

let _cache = null;          // null = unknown, true/false once fetched
let _inflight = null;

async function _refresh() {
  if (_inflight) return _inflight;
  _inflight = api.get("/billing/me").then(({ data }) => {
    _cache = !!data.is_pro;
    _inflight = null;
    return _cache;
  }).catch(() => {
    _cache = false;
    _inflight = null;
    return false;
  });
  return _inflight;
}

export function useIsPro() {
  const [isPro, setIsPro] = useState(_cache);

  useEffect(() => {
    if (_cache !== null) {
      setIsPro(_cache);
      return;
    }
    _refresh().then(setIsPro);
  }, []);

  const requirePro = useCallback((featureUrl, action) => {
    // Iter180 — Optimistically proceed while isPro is still resolving
    // (state === null). The backend enforces Pro tiering via a 402, so
    // a spurious modal on first-open (before /billing/me returns) is a
    // pure UX bug — we simply proceed and let the network response gate
    // instead. Once isPro flips to true/false, subsequent clicks are
    // gated normally.
    if (isPro || isPro === null) {
      action?.();
      return true;
    }
    try {
      window.dispatchEvent(new CustomEvent("markovlab:upgrade-required", {
        detail: { url: featureUrl || "" },
      }));
    } catch {}
    return false;
  }, [isPro]);

  return { isPro, requirePro };
}

export function invalidateProCache() {
  _cache = null;
}
